<template>
  <expandable-block class="initiatives-filter" :highlight="hasFilterChanged" v-model="isExpanded">
    <div class="flex-grow-1 initiatives-filter-wrapper" v-if="isExpanded">
      <div class="d-flex flex-wrap">
        <slot name="start"></slot>
        <div class="multi-select-filter filter-field field-block" v-if="hasFilter('teamIds')">
          <team-selection-field
            class="w-100"
            :options="teamsOptions"
            :sub-org-id="subOrgId"
            :value="filterDetails.team"
            @change="onFilter('teamIds', $event)"
          />
        </div>
        <filter-dropdown
          label="Timeline"
          class="filter-field field-block"
          @change="onFilter('timeline', $event)"
          :options="timelineOptions"
          :selected="filterDetails.timeline.label"
          v-if="hasFilter('timeline')"
        />
        <filter-dropdown
          label="Resolution"
          class="filter-field field-block"
          @change="onFilter('resolution', $event)"
          :options="resolutionOptions"
          :selected="filterDetails.resolution && filterDetails.resolution.label"
          v-if="hasFilter('resolution')"
        />
        <filter-dropdown
          label="Show"
          class="filter-field field-block"
          :class="{ last: !selectedFilterFields || selectedFilterFields.length === 0 }"
          @change="onFilter('show', $event)"
          :options="showFilterOptions"
          :selected="filterDetails.show && filterDetails.show.label"
          v-if="isTasksFeatureEnabled && hasFilter('show')"
        >
          <template v-slot:info>
            <div>
              <div>
                <div class="bold text-small-1 mb-2">Tasks collapsed under Initiatives</div>
                <img src="@/assets/collapsed-tasks.svg" alt="collapsed-tasks" />
              </div>
              <div class="mt-3">
                <div class="bold text-small-1 mb-2">Initiatives and Tasks flattened</div>
                <img src="@/assets/flattened-tasks.svg" alt="flattened-tasks" />
              </div>
            </div>
          </template>
        </filter-dropdown>
        <template v-if="selectedFilterFields">
          <template v-for="(field, index) in selectedFilterFields">
            <with-close-icon
              class="filter-field field-block"
              item-wrapper-class="flex-grow-1"
              :class="{
                last: index === selectedFilterFields.length - 1,
                'w-100': field.id === 'nql'
              }"
              :show-shadow-on-icon="true"
              dismiss-title="Remove filter"
              icon-alignment="top"
              @close="removeFilterField(index)"
              :key="field.id + '-' + index"
              v-if="field"
            >
              <form
                @submit.prevent
                class="input-field-filter text-medium"
                ref="searchForm"
                v-if="isSearchField(field)"
              >
                <label for="search">Search:</label>
                <input
                  :value="filterDetails.search"
                  @keyup.enter="triggerSearch"
                  @search="triggerSearch"
                  autocomplete="on"
                  class="search-input"
                  type="search"
                  id="search"
                  placeholder="Type and hit enter"
                />
              </form>
              <advanced-filter
                :value="filterDetails.nql"
                :filter-configuration="filterConfiguration"
                :org-id="orgId"
                :sub-org-id="subOrgId"
                @change="triggerNql"
                v-else-if="isNQLField(field)"
              />
              <condition-selector
                :org-id="orgId"
                :field="field"
                :value="getFilterValue(index)"
                :filter-configuration="filterConfiguration"
                :timeline="filterData.timeline"
                @change="onFilterOpChange(index, field.id, $event)"
                v-else
              />
            </with-close-icon>
          </template>
        </template>
        <div class="field-block">
          <add-filter-field-dropdown
            :org-id="orgId"
            :fields="filtersList"
            @select="onFilterAddition"
          />
        </div>
        <div class="filter-actions d-flex align-items-center flex-wrap">
          <save-user-list
            class="field-block"
            :filter-data="filterData"
            :sub-org-id="subOrgId"
            :org-id="orgId"
            :team-id="teamId"
            :hide-save-as-list="hideSaveAsList"
            :show-save-as-list="showSaveAsList"
            :entity-type="ENTITY_TYPE.INITIATIVE"
            :has-filter-changed="hasFilterChanged"
            @reset="resetFilters"
            @save-changes="$emit('save-list-changes')"
          />
          <options-menu
            v-if="!hideOptionsMenu"
            class="options-menu"
            :options="[
              { id: 'download', label: 'Download' },
              { id: 'download-options', label: 'Download options...' }
            ]"
            @select="downloadOptionSelected"
          />
        </div>
      </div>
    </div>
    <download-initiatives-modal
      v-model="showDownloadOptionsModal"
      @download="$emit('download', $event)"
    />
  </expandable-block>
</template>

<script>
import { trackEvent } from '@/analytics'
import ConditionSelector from '@/components/conditionSelector/ConditionSelector.vue'
import ExpandableBlock from '@/components/ExpandableBlock.vue'
import FilterDropdown from '@/components/FilterDropdown.vue'
import TeamSelectionField from '@/components/filters/TeamSelectionField.vue'
import OptionsMenu from '@/components/form/dropdown/OptionsMenu.vue'
import AddFilterFieldDropdown from '@/components/initiative/filter/AddFilterFieldDropdown.vue'
import AdvancedFilter from '@/components/initiative/advanced-filter/AdvancedFilter.vue'
import DownloadInitiativesModal from '@/components/initiative/DownloadInitiativesModal.vue'
import SaveUserList from '@/components/initiative/SaveUserList.vue'
import WithCloseIcon from '@/components/WithCloseIcon.vue'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import { featureFlags } from '@/constants/featureFlags'
import {
  FILTER_OP_SEPARATOR,
  SHOW_INITIATIVE_ID,
  SHOW_INITIATIVES_OPTIONS
} from '@/constants/initiative'
import { KEY_CODES } from '@/constants/keyCodes'
import { filterIntervals, intervalWithId, nowInterval } from '@/interval'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import TeamMixin from '@/mixins/TeamMixin'
import UserListsMixin from '@/mixins/UserListsMixin'
import debounce from '@/utils/debounce'
import hasFilterChanged from '@/utils/initiative/hasFilterChanged'
import prepareFiltersList from '@/utils/initiative/prepareFiltersList'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

const RESOLUTION_OPTIONS = [
  { label: 'Any', id: 'any' },
  { label: 'Open', id: 'open' },
  { label: 'Closed', id: 'closed' }
]

const defaultFilterState = {
  team: [{ label: 'All', id: -1 }],
  timeline: nowInterval(),
  resolution: RESOLUTION_OPTIONS.find(R.propEq('id', 'open')),
  show: SHOW_INITIATIVES_OPTIONS.find(R.propEq('id', SHOW_INITIATIVE_ID.initiatives))
}

/**
 * This is the filter component for Initiatives
 Props:
 - orgId

 Emits:
 @change: This is emitted when any filter is changed. Filter params are passed as argument
 */
export default {
  name: 'InitiativesFilter',
  components: {
    TeamSelectionField,
    WithCloseIcon,
    SaveUserList,
    FilterDropdown,
    ConditionSelector,
    AddFilterFieldDropdown,
    OptionsMenu,
    ExpandableBlock,
    DownloadInitiativesModal,
    AdvancedFilter
  },
  props: [
    'orgId',
    'subOrgId',
    'teamId',
    'projectId',
    'filterData',
    'defaultFiltersList',
    'defaultFilters',
    'hideSaveAsList',
    'showSaveAsList',
    'hideOptionsMenu',
    'collapseOnLoad'
  ],
  mixins: [ComponentIdMixin, FeaturesMixin, MembershipsMixin, TeamMixin, UserListsMixin],
  data() {
    return {
      timelineOptions: [intervalWithId('any'), ...filterIntervals()].map(interval => ({
        ...interval,
        label: interval.genericName || interval.name
      })),
      resolutionOptions: RESOLUTION_OPTIONS,
      showFilterOptions: SHOW_INITIATIVES_OPTIONS,
      isTasksFeatureEnabled: this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_TASKS),
      selectedFilterFields: null,
      loadedFilterFields: null,
      hasFilter: R.includes(R.__, this.defaultFiltersList),
      isExpanded: false,
      showDownloadOptionsModal: false,
      ENTITY_TYPE
    }
  },
  computed: {
    ...mapGetters('initiative', ['filterConfiguration']),
    isProjectsEnabled() {
      return !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    },
    filtersList() {
      let allCustomFields = this.orgCustomFields || []
      if (this.projectId) {
        // If project scope is defined, remove CFs belonging to other projects
        allCustomFields = allCustomFields.filter(
          field => !field.projectId || field.projectId === this.projectId
        )
      }
      const includeProjectField = !this.projectId && this.isProjectsEnabled
      const filtersList = prepareFiltersList(
        this.filterConfiguration,
        allCustomFields,
        includeProjectField
      )
      if (!filtersList) return []
      let list = [
        { id: 'search', label: 'Search text', fieldType: 'system_field' },
        ...filtersList,
        { id: 'nql', fieldType: 'nql' }
      ]
      //remove search or nql option from list if already added as filter
      if (this.selectedFilterFields?.find(x => this.isSearchField(x))) {
        list = list.filter(x => !this.isSearchField(x))
      }
      if (this.selectedFilterFields?.find(x => this.isNQLField(x))) {
        list = list.filter(x => !this.isNQLField(x))
      }
      return list
    },
    orgCustomFields() {
      return this.$store.state.orgCustomFields[this.orgId]
    },
    parentTeam() {
      return this.$store.getters.teamById(this.subOrgId)
    },
    teams() {
      const teamTree = this.getTeamTree()
      let teams = this.searchInTeamTree(teamTree, this.subOrgId)
      teams = this.flattenTeamTree([teams]).filter(
        team => team.isOrgLike || team.parentId === this.subOrgId
      )
      const allTeams = [this.parentTeam, ...teams].map(team => ({ ...team, label: team.name }))
      return [...defaultFilterState.team, ...allTeams]
    },
    filterDetails() {
      const filterDetails = R.toPairs(this.filterData).map(([field, fieldValue]) => {
        switch (field) {
          case 'teamIds':
            return [
              'team',
              fieldValue
                .toString()
                .split(',')
                .map(teamId => this.teams?.find(R.propEq('id', +teamId)))
                .filter(Boolean)
            ]
          case 'timeline': {
            const interval = intervalWithId(fieldValue)
            return [field, { ...interval, label: interval.genericName || interval.name }]
          }
          case 'resolution':
            return [field, this.resolutionOptions.find(R.propEq('id', fieldValue))]
          case 'show':
            return [field, this.showFilterOptions.find(R.propEq('id', fieldValue))]
          default:
            return [field, fieldValue]
        }
      })
      let defaultFilters = { ...defaultFilterState }
      return { ...defaultFilters, ...R.fromPairs(filterDetails) }
    },
    teamsOptions() {
      let teams = this.teams || []
      if (this.filterDetails.team.length > 0) {
        // remove "All teams" option
        teams = teams.filter(x => x.id !== -1)
      }
      return R.differenceWith((x, y) => x.id === y.id, teams, this.filterDetails.team)
    },
    hasFilterChanged() {
      return hasFilterChanged(
        this.defaultFilters,
        this.selectedList?.filterData,
        this.filterData,
        !!this.selectedListName
      )
    }
  },
  methods: {
    ...mapActions('initiative', ['getFilterConfiguration']),
    getSelectedListName() {
      return this.$store.getters['userLists/listById'](this.selectedListId)?.name
    },
    isSearchField(field) {
      return field?.id === 'search'
    },
    isNQLField(field) {
      return field?.id === 'nql'
    },
    getSelectedFieldsFromFilterData({ filterOp = [], search, nql }) {
      if (typeof filterOp === 'string') {
        filterOp = [filterOp]
      }
      const fields = filterOp
        .map(op => op.split(FILTER_OP_SEPARATOR)[0])
        .map(field => this.filtersList.find(f => f.id === field))
      if (search) {
        fields.push(this.filtersList.find(this.isSearchField))
      }
      if (nql) {
        fields.push(this.filtersList.find(this.isNQLField))
      }
      return fields
    },
    getFilterValue(index) {
      let filterOp = [this.filterDetails.filterOp || []].flat()
      const searchFieldIndex = this.selectedFilterFields.findIndex(this.isSearchField)
      if (searchFieldIndex > -1) {
        filterOp = R.insert(searchFieldIndex, null, filterOp)
      }
      return filterOp[index]
    },
    getFilterOpValue(fieldValue, seqNumber) {
      let filterOp = this.filterData.filterOp
      if (!filterOp) {
        filterOp = fieldValue
      } else if (typeof filterOp === 'string') {
        if (seqNumber === 0) {
          filterOp = fieldValue
        } else {
          // new field has been added
          filterOp = [filterOp, fieldValue]
        }
      } else if (filterOp instanceof Array) {
        if (seqNumber >= filterOp.length) {
          // new field has been added
          filterOp = [...filterOp, fieldValue]
        } else {
          filterOp = R.update(seqNumber, fieldValue, filterOp)
        }
      }
      return filterOp
    },
    onFilterOpChange(index, field, fieldValue) {
      let fieldSequenceNumber = index
      const searchFieldIndex = this.selectedFilterFields.findIndex(this.isSearchField)
      if (searchFieldIndex > -1 && searchFieldIndex < index) {
        fieldSequenceNumber -= 1
      }
      fieldValue = this.getFilterOpValue(fieldValue, fieldSequenceNumber)
      this.emitChange({
        field: 'filterOp',
        fieldValue
      })
    },
    onFilter(field, fieldValue) {
      this.emitChange({ field, fieldValue })
    },
    emitChange(filterData) {
      this.$emit('change', filterData)
    },
    resetFilters() {
      this.selectedFilterFields = this.loadedFilterFields
      this.$emit('reset')
    },
    triggerSearch: debounce(function(event) {
      if (event.keyCode === KEY_CODES.ENTER) {
        event.target.blur()
        trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.SEARCH, EVENT_NAME.ALL_INITIATIVES)
      }
      this.onFilter('search', event.target.value)
    }, 100),
    triggerNql(value) {
      this.onFilter('nql', value)
    },
    onFilterAddition(filter) {
      this.selectedFilterFields.push(filter)
      this.onFilterOpChange(this.selectedFilterFields.length - 1, filter.id, '')
    },
    removeFilterField(index) {
      const fieldToRemove = this.selectedFilterFields[index]
      this.selectedFilterFields = R.remove(index, 1, this.selectedFilterFields)
      this.$emit('remove-filter', { index, field: fieldToRemove })
    },
    downloadOptionSelected(option) {
      if (option.id === 'download') {
        this.$emit('download', { format: 'xlsx' })
      } else if (option.id === 'download-options') {
        this.showDownloadOptionsModal = true
      } else {
        console.warn('Got unknown download option', option)
      }
    },
    evaluateDefaultExpansion() {
      return !this.userSavedLists
        ? false
        : !this.collapseOnLoad && (this.hasFilterChanged || !this.getSelectedListName())
    }
  },
  async mounted() {
    if (!this.filterConfiguration) {
      await this.getFilterConfiguration()
    }
    this.isExpanded = this.evaluateDefaultExpansion()
    if (this.orgCustomFields) {
      const loadedFilterFields = this.getSelectedFieldsFromFilterData(this.filterData)
      this.loadedFilterFields = loadedFilterFields
      this.selectedFilterFields = loadedFilterFields
    }
    this.updateMemberships(this.orgId)
  },
  watch: {
    orgCustomFields() {
      if (!this.selectedFilterFields && this.orgCustomFields) {
        const loadedFilterFields = this.getSelectedFieldsFromFilterData(this.filterData)
        this.loadedFilterFields = loadedFilterFields
        this.selectedFilterFields = loadedFilterFields
      }
    },
    userSavedLists(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.isExpanded = this.evaluateDefaultExpansion()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_badge.scss';
@import './styles/filter';

/deep/ .multi-select .field {
  border-color: $quaternary;
  height: 100%;
}

/deep/ .multi-select {
  height: 100%;
}

.field-block {
  margin-top: 5px;
  margin-right: 8px;
  white-space: nowrap;
  display: flex;
  align-self: center;
}

.filter-field:not(.last):after {
  content: 'AND';
  font-size: 0.875rem;
  color: $secondary;
  align-self: center;
  margin-left: 8px;
}

.initiatives-filter-wrapper {
  margin-top: -12px;
}

.any-user {
  padding-left: 0px !important;
}

.clear-due-date {
  cursor: pointer;
  padding-left: 4px;
  height: 18px;
  width: 18px;
  background-color: $quaternary;
  border-radius: 50%;
  background-position: center center;
}

.options-menu {
  margin-top: 5px;
}

input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none; // remove x icon from search input
}

.vertical-line {
  border-right: 1px solid $borderTertiary;
  width: 1px;
  height: 25px;
}

@include media-breakpoint-up(xl) {
  .search-input {
    &:focus,
    &:not(:placeholder-shown) {
      width: 465px;
    }
  }
}

.options-menu {
  opacity: 0;
}

.initiatives-filter:hover .options-menu {
  opacity: 1;
}

@include media-breakpoint-down(md) {
  .field-block {
    flex-grow: 0;
    margin-top: 12px;
  }
  .initiatives-filter-wrapper {
    justify-content: flex-start !important;
  }
  .filter-field {
    flex-grow: 1;
  }
}

@include media-breakpoint-down(sm) {
  .field-block {
    flex-basis: 100%;
  }
  /deep/ .filter-toggle-field {
    max-width: initial;
  }
}
</style>
